/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import React from 'react'
import { useField, useFormikContext } from 'formik'
import type { Country } from 'react-phone-number-input'
import type { Province } from '@sportninja/common/hooks/useProvinces'
import ModernPhoneInput from 'src/components/ts-components/ModernPhoneInput'
import { ProvinceSelectInput } from 'src/components/ts-components/ProvinceSelectInput'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import Button from 'src/components/ts-components/Button'
import { ColorPickerInput } from 'src/components/ts-components/ColorPickerInput'
import { NumberInput } from 'src/components/ts-components/NumberInput'
import Text from 'src/components/ts-components/Text'
import { TextAreaInput } from 'src/components/ts-components/TextAreaInput'
import { TextInput } from 'src/components/ts-components/TextInput'

/**
 * Formik ↔ shared-input bridges for account settings forms: the SportsPay
 * application, the API / Plugin Access keys and the Website Plugin settings.
 *
 * Mirrors the Edit Player flow's `FormikField.tsx`. Note the shared inputs do
 * NOT agree on an error-prop shape — TextInput/SelectInput take
 * `fieldErrors`/`customError` records while DateOfBirthInput wants an array of
 * names — so each wrapper adapts its own.
 */

/** Build the (fieldErrors, customError) record pair most shared inputs expect. */
const useErrorProps = (name: string) => {
  const [, meta] = useField(name)
  const show = Boolean(meta.touched && meta.error)
  const fieldErrors = show ? { [name]: meta.error } : {}
  const customError = show ? { [name]: meta.error } : {}
  return { fieldErrors, customError }
}

/**
 * Returns a value setter that also clears the field's error on edit. The
 * server-validated forms run with validateOnChange off and have no client-side
 * rules at all, so a server-set error would otherwise linger after the user
 * fixes the field. A form validating on change (the Website Plugin snippet
 * builder) re-validates right after, so its errors come back where still due.
 *
 * The edit also marks the field touched, so a client-side error shows as soon
 * as the field is edited (the controls without a blur, like switches, have no
 * other moment to).
 */
const useClearErrorOnChange = <T,>(name: string) => {
  const [, meta, helpers] = useField(name)
  const { setFieldError } = useFormikContext()
  return (value: T) => {
    helpers.setValue(value)
    if (!meta.touched) {
      helpers.setTouched(true, false)
    }
    if (meta.error) {
      setFieldError(name, undefined)
    }
  }
}

/** Marks the field touched on blur, without re-validating. */
const useTouchOnBlur = (name: string) => {
  const [, meta, helpers] = useField(name)
  return () => {
    if (!meta.touched) {
      helpers.setTouched(true, false)
    }
  }
}

/**
 * What an emptied optional field stores: `'unset'` removes the key (Formik
 * deletes a path set to `undefined`), `'null'` stores `null`.
 */
type EmptyValue = 'unset' | 'null'

const emptyValueOf = (empty: EmptyValue): null | undefined =>
  empty === 'null' ? null : undefined

/** Small grey helper line rendered under a field, matching SportsPay's copy. */
const FieldHint: React.FC<{ children: React.ReactNode; color?: string }> = ({
  children,
  color = colors.NEUTRAL_50,
}) => (
  <Text variant='paragraphXSmall' color={color}>
    {children}
  </Text>
)

interface BaseProps {
  name: string
  label: string
  hint?: string
  disabled?: boolean
  placeholder?: string
}

/** Text field bound to Formik; its hint may carry a link. */
export const TextField: React.FC<
  Omit<BaseProps, 'hint'> & {
    hint?: React.ReactNode
    autoComplete?: 'organization' | 'name' | 'url'
    /** For an optional field: what clearing it stores, instead of `''`. */
    emptyValue?: EmptyValue
  }
> = ({
  name,
  label,
  hint,
  disabled,
  placeholder,
  autoComplete,
  emptyValue,
}) => {
  const [field] = useField(name)
  const setValue = useClearErrorOnChange<string | null | undefined>(name)
  const onBlur = useTouchOnBlur(name)
  const { fieldErrors, customError } = useErrorProps(name)

  return (
    <div css={fieldCellStyle}>
      <TextInput
        name={name}
        label={label}
        value={field.value ?? ''}
        setValue={(text) =>
          setValue(text === '' && emptyValue ? emptyValueOf(emptyValue) : text)
        }
        onBlur={onBlur}
        fieldErrors={fieldErrors}
        customError={customError}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
      {hint ? <FieldHint>{hint}</FieldHint> : null}
    </div>
  )
}

/** Select field bound to Formik. */
export const SelectField: React.FC<
  BaseProps & {
    options: { label: string; value: string }[]
    /**
     * Defaults to the muted `NEUTRAL_50`; pass `colors.PRIMARY_300` when the
     * hint carries a consequence the user should notice.
     */
    hintColor?: string
  }
> = ({ name, label, hint, hintColor, disabled, placeholder, options }) => {
  const [field] = useField(name)
  const setValue = useClearErrorOnChange<string>(name)
  const { fieldErrors, customError } = useErrorProps(name)

  return (
    <div css={fieldCellStyle}>
      <SelectInput
        name={name}
        label={label}
        options={options}
        value={field.value ?? ''}
        onChange={(v) => setValue(v ?? '')}
        fieldErrors={fieldErrors}
        customError={customError}
        disabled={Boolean(disabled)}
        placeholder={placeholder}
      />
      {hint ? <FieldHint color={hintColor}>{hint}</FieldHint> : null}
    </div>
  )
}

/** Canada — the only country SportsPay onboards through this form. */
const COUNTRY_ID_CANADA = 36

/** Same restriction, as the ISO 3166-1 alpha-2 code the phone input wants. */
const PHONE_COUNTRIES: Country[] = ['CA']

/**
 * Phone field, matching the Edit Player profile's `PhoneField` — the full E.164
 * value is stored, so the country is not tracked separately.
 *
 * Differs in one way: the country selector is pinned to Canada rather than
 * detected from the browser locale, because this application is Canada-only.
 */
export const PhoneField: React.FC<BaseProps> = ({
  name,
  label,
  hint,
  disabled,
}) => {
  const [field, meta] = useField(name)
  const setValue = useClearErrorOnChange<string>(name)

  return (
    <div css={fieldCellStyle}>
      <ModernPhoneInput
        label={label}
        value={field.value ?? ''}
        onChange={setValue}
        disabled={disabled}
        error={meta.touched && meta.error ? meta.error : undefined}
        countries={PHONE_COUNTRIES}
        forcedDefaultCountry='CA'
      />
      {hint ? <FieldHint>{hint}</FieldHint> : null}
    </div>
  )
}

/**
 * Province picker bound to Formik, locked to Canada. Uses the same shared
 * `ProvinceSelectInput` as the Edit Player profile, so the list stays in one
 * place; no country picker is rendered because this application is Canada-only.
 *
 * Writes TWO Formik fields. `ProvinceSelectInput` accepts only a numeric
 * province id as its value — it silently rejects anything else (see its
 * `handleChange`) — but SportsPay wants the ISO 3166-2 code. So the picker
 * keeps the id in `name`, and the matching `iso_3166_2` is mirrored into
 * `codeName` for the mapper to submit.
 */
export const ProvinceField: React.FC<
  BaseProps & {
    /** Companion field holding the resolved ISO code (e.g. 'ON'). */
    codeName: string
  }
> = ({ name, codeName, label, disabled, placeholder }) => {
  const [field] = useField(name)
  const setValue = useClearErrorOnChange<string>(name)
  const { fieldErrors, customError } = useErrorProps(name)
  const { setFieldValue } = useFormikContext()

  // Filled once the provinces resolve; read only inside the effect below.
  const provincesRef = React.useRef<Province[]>([])
  const selectedId = field.value ?? ''

  /**
   * Keep the ISO code in step with the picker. Runs on selection AND on the
   * provinces arriving, because the list is fetched — a value restored before
   * the fetch settles would otherwise never resolve to a code.
   */
  const syncCode = React.useCallback(() => {
    const match = provincesRef.current.find(
      (province) => province.id.toString() === selectedId
    )
    setFieldValue(codeName, match?.iso_3166_2 ?? '', false)
  }, [selectedId, codeName, setFieldValue])

  React.useEffect(() => {
    syncCode()
  }, [syncCode])

  return (
    <div css={fieldCellStyle}>
      <ProvinceSelectInput
        name={name}
        label={label}
        value={selectedId}
        onChange={(v) => setValue(v ?? '')}
        countryId={COUNTRY_ID_CANADA}
        provincesRef={provincesRef}
        onProvincesLoaded={syncCode}
        fieldErrors={fieldErrors}
        customError={customError}
        disabled={disabled}
        placeholder={placeholder}
      />
    </div>
  )
}

/** Multi-line text field bound to Formik. */
export const TextAreaField: React.FC<BaseProps & { rows?: number }> = ({
  name,
  label,
  hint,
  disabled,
  placeholder,
  rows,
}) => {
  const [field] = useField(name)
  const setValue = useClearErrorOnChange<string>(name)
  const { fieldErrors, customError } = useErrorProps(name)

  return (
    <div css={fieldCellStyle}>
      <TextAreaInput
        name={name}
        label={label}
        value={field.value ?? ''}
        setValue={setValue}
        fieldErrors={fieldErrors}
        customError={customError}
        disabled={disabled}
        placeholder={placeholder}
        rows={rows}
      />
      {hint ? <FieldHint>{hint}</FieldHint> : null}
    </div>
  )
}

/**
 * On/off field bound to Formik. The switch has no inline error slot, so a
 * server error on it is shown as a hint line underneath.
 */
export const SwitchField: React.FC<
  Pick<BaseProps, 'name' | 'label' | 'hint' | 'disabled'>
> = ({ name, label, hint, disabled }) => {
  const [field, meta] = useField<boolean>(name)
  const setValue = useClearErrorOnChange<boolean>(name)
  const error = meta.touched && meta.error ? meta.error : null

  return (
    <div css={fieldCellStyle}>
      <div css={switchRowStyle}>
        <SwitchInput
          title={label}
          subtitle={hint}
          value={Boolean(field.value)}
          onChange={setValue}
          disabled={disabled}
        />
      </div>
      {error ? (
        <Text variant='paragraphXSmall' color={colors.ERROR_LIGHT}>
          {error}
        </Text>
      ) : null}
    </div>
  )
}

/**
 * Colour field bound to Formik, holding `#RRGGBB`. The picker reports hex
 * without the `#`, in lowercase.
 *
 * With `defaultColour` the field is optional: while unset (`undefined`) the
 * swatch shows that colour and says so, and once set a Reset unsets it again.
 */
export const ColorField: React.FC<
  Pick<BaseProps, 'name' | 'label' | 'disabled'> & {
    /** The colour in effect while the field is unset. */
    defaultColour?: string
  }
> = ({ name, label, disabled, defaultColour }) => {
  const [field, , helpers] = useField<string | undefined>(name)
  const setValue = useClearErrorOnChange<string | undefined>(name)
  const { fieldErrors, customError } = useErrorProps(name)
  const isUnset = field.value === undefined

  return (
    <div css={fieldCellStyle}>
      <ColorPickerInput
        name={name}
        label={label}
        value={field.value ?? defaultColour ?? ''}
        setValue={(hex) => setValue(`#${hex.toUpperCase()}`)}
        fieldErrors={fieldErrors}
        customError={customError}
        disabled={disabled}
      />
      {defaultColour === undefined ? null : isUnset ? (
        <FieldHint>Default</FieldHint>
      ) : (
        <Button
          size='compact'
          variant='ghost'
          text={capitalizeWords('Reset')}
          disabled={disabled}
          onClick={() => helpers.setValue(undefined)}
        />
      )}
    </div>
  )
}

/**
 * Number field bound to Formik, for an optional number: empty stores
 * `emptyValue`, anything else `Number(text)` — a half-typed `-` is `NaN`, so
 * the form's own rules report it. The typed text is kept as a draft, so `1.`
 * stays on screen while it means 1; a value set from outside replaces it.
 *
 * No min/max reach `NumberInput`: it clamps per keystroke, which turns the
 * first digit of `18` into the minimum. Put the range in `hint` instead.
 */
export const NumberField: React.FC<
  BaseProps & {
    emptyValue: EmptyValue
  }
> = ({ name, label, hint, disabled, placeholder, emptyValue }) => {
  const [field] = useField<number | null | undefined>(name)
  const setValue = useClearErrorOnChange<number | null | undefined>(name)
  const onBlur = useTouchOnBlur(name)
  const { fieldErrors, customError } = useErrorProps(name)
  const [draft, setDraft] = React.useState('')

  const parse = (text: string) =>
    text === '' ? emptyValueOf(emptyValue) : Number(text)
  const stored = field.value ?? emptyValueOf(emptyValue)
  const shown = Object.is(parse(draft), stored)
    ? draft
    : stored === null || stored === undefined
    ? ''
    : String(stored)

  return (
    <div css={fieldCellStyle}>
      <NumberInput
        name={name}
        label={label}
        value={shown}
        setValue={(text) => {
          setDraft(text)
          setValue(parse(text))
        }}
        onBlur={onBlur}
        fieldErrors={fieldErrors}
        customError={customError}
        disabled={disabled}
        placeholder={placeholder}
      />
      {hint ? <FieldHint>{hint}</FieldHint> : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

/**
 * The shared inputs render label, control and error as sibling fragments with
 * no wrapper. This stacks them (label on top) and makes the cell one grid item.
 *
 * Deliberately does NOT reserve a fixed label height. Labels here vary a lot
 * ("City" beside "Organization's Main E-mail Address"), and padding every label
 * out to the tallest one leaves an obvious gap above the inputs whenever the
 * labels happen to fit on one line. The row layout solves the wrapping case
 * instead — see `twoColumnStyle` in SportsPaySignupForm, which drops to one
 * column while the labels still fit.
 */
const fieldCellStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
`

/** Label and hint on the left, the switch beside them on the right. */
const switchRowStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  align-self: stretch;
  min-width: 0;
`
